import { useEffect, useState } from "react";
import Layout from "./Layout";
import {
  getCartApi,
  createOrderApi,
  verifyPaymentApi,
  clearCartApi,
} from "../services/api";
import { getGuestUserId } from "../utils/guestUser";
import { STATE_CITY_MAP } from "../utils/stateCityMap";

export default function Checkout() {
  const [cartItems, setCartItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
  });

  const [errors, setErrors] = useState({});

  const RAZORPAY_TEST_KEY_ID = process.env.REACT_APP_RAZORPAY_LIVE_KEY_ID;

  useEffect(() => {
    const userId = getGuestUserId();
    getCartApi(userId)
      .then((items) => {
        setCartItems(items);
        const total = items.reduce(
          (acc, item) => acc + item.price * item.quantity,
          0,
        );
        setTotalPrice(total);
      })
      .finally(() => setLoading(false));
  }, []);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = "Name is required";
    if (!/^[6-9]\d{9}$/.test(form.phone))
      newErrors.phone = "Enter valid number";
    if (!form.address.trim()) newErrors.address = "Address required";
    if (!form.state) newErrors.state = "State required";
    if (!form.city) newErrors.city = "City required";
    if (!/^\d{6}$/.test(form.pincode))
      newErrors.pincode = "Valid 6-digit pincode required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const loadRazorpay = () =>
    new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handleCheckout = async () => {
    if (!validate()) return;

    const userId = getGuestUserId();
    const loaded = await loadRazorpay();
    if (!loaded) return alert("Razorpay failed to load");

    const data = await createOrderApi(totalPrice, userId, form, cartItems);
    if (!data.success) return alert("Order creation failed");
    console.log("ORDER DATA", data);
    const options = {
      key: RAZORPAY_TEST_KEY_ID,
      amount: data.order.amount,
      currency: "INR",
      name: "Legacy Decor",
      description: "Order Payment",
      order_id: data.order.id,

      prefill: {
        name: form.name,
        email: form.email,
        contact: form.phone,
      },

      notes: {
        address: form.address,
      },

      handler: async (response) => {
        const verifyData = await verifyPaymentApi(response);
        if (verifyData.success) {
          await clearCartApi(userId);
          //window.location.href = "/success";
        } else {
          alert("Payment verification failed");
        }
      },
      theme: { color: "#FEA198" },
    };
    console.log("key", RAZORPAY_TEST_KEY_ID);
    console.log("order id", data.order.id);
    console.log("amount", data.order.amount);
    new window.Razorpay(options).open();
  };

  return (
    <Layout>
      <section className="px-8 py-12 max-w-4xl mx-auto">
        <h1 className="text-4xl font-serif text-light_primary mb-8">
          Checkout
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 ">
          {/* Name */}
          <div>
            <label className="block text-sm mb-1 text-black">Full Name</label>
            <input
              value={form.name}
              onChange={(e) => updateForm("name", e.target.value)}
              className="w-full bg-footer_clr px-4 py-2 rounded-lg"
            />
            {errors.name && (
              <p className="text-red-400 text-sm">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm mb-1 text-black">Email</label>
            <input
              value={form.email}
              onChange={(e) => updateForm("email", e.target.value)}
              className="w-full bg-footer_clr px-4 py-2 rounded-lg"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm mb-1 text-black">
              Phone Number
            </label>
            <input
              value={form.phone}
              onChange={(e) => updateForm("phone", e.target.value)}
              className="w-full bg-footer_clr px-4 py-2 rounded-lg"
            />
            {errors.phone && (
              <p className="text-red-400 text-sm">{errors.phone}</p>
            )}
          </div>

          {/* Address */}
          <div className="sm:col-span-2">
            <label className="block text-sm mb-1 text-black">Address</label>
            <input
              value={form.address}
              onChange={(e) => updateForm("address", e.target.value)}
              className="w-full bg-footer_clr px-4 py-2 rounded-lg"
            />
            {errors.address && (
              <p className="text-red-400 text-sm">{errors.address}</p>
            )}
          </div>

          {/* State */}
          <div>
            <label className="block text-sm mb-1 text-black">State</label>
            <select
              value={form.state}
              onChange={(e) => {
                updateForm("state", e.target.value);
                updateForm("city", "");
              }}
              className="w-full bg-footer_clr px-4 py-2 rounded-lg"
            >
              <option value="">Select State</option>
              {Object.keys(STATE_CITY_MAP).map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
            {errors.state && (
              <p className="text-red-400 text-sm">{errors.state}</p>
            )}
          </div>

          {/* City */}
          <div>
            <label className="block text-sm mb-1 text-black">City</label>
            <select
              value={form.city}
              disabled={!form.state}
              onChange={(e) => updateForm("city", e.target.value)}
              className="w-full bg-footer_clr px-4 py-2 rounded-lg disabled:opacity-50"
            >
              <option value="">Select City</option>
              {form.state &&
                STATE_CITY_MAP[form.state].map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
            </select>
            {errors.city && (
              <p className="text-red-400 text-sm">{errors.city}</p>
            )}
          </div>

          {/* Pincode */}
          <div>
            <label className="block text-sm mb-1 text-black">Pincode</label>
            <input
              value={form.pincode}
              onChange={(e) => updateForm("pincode", e.target.value)}
              className="w-full bg-footer_clr px-4 py-2 rounded-lg"
            />
            {errors.pincode && (
              <p className="text-red-400 text-sm">{errors.pincode}</p>
            )}
          </div>
        </div>

        <button
          onClick={handleCheckout}
          className="mt-6 bg-primary text-white px-8 py-3 rounded-lg w-full sm:w-auto"
        >
          Pay Now
        </button>
      </section>
    </Layout>
  );
}
